import { useRecoilCallback, useRecoilValue } from 'recoil';
import type { HangingProtocol } from './useHangingProtocol';
import {
  isPreviewingHangingProtocolState,
  previewHangingProtocolSelector,
  previousHangingProtocolState,
  selectedHangingProtocolPayloadSelector,
  selectedHangingProtocolSmidSelector,
} from './state';
import { useSelectedHangingProtocolWriters } from './useSelectedHangingProtocol';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { client } from 'modules/Apollo/client';
import { GET_HANGING_PROTOCOL } from 'modules/Apollo/queries';
import { useState } from 'react';
import { usePreviousHangingProtocol } from './usePreviousHangingProtocol';
import { useExamGroupedPriorStudies, usePriorStudyGroups } from './useStudiesGroupedAsCases';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags/suspense';
import { WithApolloContext } from 'modules/Apollo/types';
import { logger } from 'modules/logger';

/**
 * Applies a preview and reports what it managed to load: the previewed protocol, or `null` when the
 * fetch failed or there was no case to resolve it against. Callers that give something up in order to
 * show the preview -- the confirm-discard path discards unsaved clinical edits -- must be able to
 * distinguish "previewing" from "nothing happened".
 */
export type PreviewHangingProtocol = (id: string) => Promise<Nullable<HangingProtocol>>;

export type UsePreviewHangingProtocol = {
  previewHangingProtocol: PreviewHangingProtocol;
  confirmPreview: () => void;
  selectHangingProtocol: (smid: string) => Promise<void>;
  clearPreviewState: () => void;
  rejectAndRemovePreview: () => void;
  refreshSelectedHangingProtocol: () => Promise<void>;
  clearSelectedHangingProtocol: () => void;
  loadingPreview: boolean;
};

export const usePreviewHangingProtocolConfiguration = (): Nullable<HangingProtocol> => {
  const currentCaseSmid = useCurrentCaseId();
  return useRecoilValue(previewHangingProtocolSelector(currentCaseSmid));
};

export const usePreviewHangingProtocol = ({
  context,
}: WithApolloContext): UsePreviewHangingProtocol => {
  const enableExamGrouping = useFeatureFlagEnabled(FF.EXAM_GROUPED_HANGING_PROTOCOLS);
  const studiesGroupedByExam = useExamGroupedPriorStudies({ context });
  const ungroupedStudies = usePriorStudyGroups({ context });
  const cases = enableExamGrouping ? studiesGroupedByExam : ungroupedStudies;
  const { selectHangingProtocol, refreshSelectedHangingProtocol, clearSelectedHangingProtocol } =
    useSelectedHangingProtocolWriters({ cases, context });
  const { savePreviousHangingProtocol } = usePreviousHangingProtocol({
    context,
  });

  const [loadingPreview, setLoadingPreview] = useState(false);

  const currentCaseSmid = useCurrentCaseId();

  const previewHangingProtocol = useRecoilCallback(
    ({ set, snapshot }) => {
      return async (id: string) => {
        if (currentCaseSmid == null || cases.length === 0) return null;
        setLoadingPreview(true);
        const isAlreadyPreviewing = snapshot
          .getLoadable(isPreviewingHangingProtocolState)
          .getValue();
        if (!isAlreadyPreviewing) {
          savePreviousHangingProtocol();
          set(isPreviewingHangingProtocolState, true);
        }

        try {
          const { data } = await client.query({
            query: GET_HANGING_PROTOCOL,
            variables: { id, cases },
            context: {
              ...context,
              telemetry: {
                ...(context.telemetry ?? {}),
                fetchPolicy: 'no-cache',
              },
            },
            // GET_HANGING_PROTOCOL resolves viewport study/series/stack references against
            // the *current case*, but HangingProtocol normalizes by smid in the shared
            // cache — writing preview results into the cache would overwrite the canonical
            // (non-case-resolved) entity that the HP list table reads from, making the
            // previewed HP appear mutated there. no-cache skips both the cache read and
            // write for this query, keeping preview fetches fully isolated.
            fetchPolicy: 'no-cache',
          });
          const hangingProtocol = data?.hangingProtocol;

          logger.info(`Previewed Hanging Protocol ${hangingProtocol?.smid}`);

          set(previewHangingProtocolSelector(currentCaseSmid), hangingProtocol);
          setLoadingPreview(false);
          return hangingProtocol ?? null;
        } catch (error: any) {
          logger.error('Failed to fetch hanging protocol', error);
          setLoadingPreview(false);
          // Reported rather than only logged: a caller that discards local state in order to make
          // room for the preview has to be able to tell that no preview arrived, or a transient
          // fetch failure becomes silent data loss (see the confirm-discard path in
          // HangingProtocolWidget).
          return null;
        }
      };
    },
    [cases, context, currentCaseSmid, savePreviousHangingProtocol]
  );

  // Discard-only cleanup: clears preview state without recording it as the case's selection.
  // Used when preview state needs to be reset as a side effect of an unrelated action (e.g.
  // submitting the HP create/update form), where promoting an unconfirmed preview would be wrong.
  const clearPreviewState = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        if (currentCaseSmid == null) return;

        reset(previewHangingProtocolSelector(currentCaseSmid));
        set(isPreviewingHangingProtocolState, false);
        reset(previousHangingProtocolState);
      },
    [currentCaseSmid]
  );

  // Confirm: record the previewed HP as the case's durable selection, then delegate to
  // clearPreviewState for the shared cleanup. This must only run when the user explicitly clicks
  // Confirm — never as incidental cleanup (e.g. an unrelated HP form submit), since that would
  // silently make an unconfirmed preview durable.
  //
  // Seeds the payload cache from the preview rather than re-fetching: the preview was already
  // fetched against the current case, so it is exactly what resolving the new selection would ask
  // the server for.
  const confirmPreview = useRecoilCallback(
    ({ set, snapshot }) =>
      () => {
        if (currentCaseSmid == null) return;

        const previewedHangingProtocol = snapshot
          .getLoadable(previewHangingProtocolSelector(currentCaseSmid))
          .getValue();
        if (previewedHangingProtocol != null) {
          set(selectedHangingProtocolSmidSelector(currentCaseSmid), previewedHangingProtocol.smid);
          set(selectedHangingProtocolPayloadSelector(currentCaseSmid), previewedHangingProtocol);
        }

        // viewport/layout resets are handled by resetToHP in useHangingProtocol
        clearPreviewState();
      },
    [currentCaseSmid, clearPreviewState]
  );

  // Reject: discard the preview entirely. The visible HP falls back to selectedHangingProtocol ??
  // matchedHangingProtocol via useHangingProtocol's priority chain, which is exactly what
  // previousHangingProtocol snapshotted at preview-start time (see usePreviousHangingProtocol) — so
  // there is no need to write it back into the preview atom. Writing it back instead left a stale
  // value in previewHangingProtocolSelector that outlived the cancel and kept winning over later
  // clearSelectedHangingProtocol() calls (e.g. Reset, delete) (VX-4531).
  const rejectAndRemovePreview = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        if (currentCaseSmid == null) return;
        set(isPreviewingHangingProtocolState, false);
        reset(previewHangingProtocolSelector(currentCaseSmid));
        reset(previousHangingProtocolState);
      },
    [currentCaseSmid]
  );

  return {
    previewHangingProtocol,
    confirmPreview,
    selectHangingProtocol,
    clearPreviewState,
    rejectAndRemovePreview,
    refreshSelectedHangingProtocol,
    clearSelectedHangingProtocol,
    loadingPreview,
  };
};
